import React, { useState, useRef } from "react";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { adminInitial, adminValidationSchema } from "../common/Validation";
import Spinwheel from "../common/Spinwheel";
import { loginAdmin, sendOtpApi, verifyOtpApi } from "../../utils/ApiEndpointFunctions";

const Login = () => {
  const [loader, setLoader] = useState(false);
  const [step, setStep] = useState(1); // 1 = Email+Password, 2 = OTP
  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputsRef = useRef([]);
  const [resendTimer, setResendTimer] = useState(0); // seconds left for resend
  const [resendLoading, setResendLoading] = useState(false);
  // Resend OTP logic
  React.useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      const otpRes = await sendOtpApi({ email: formik.values.email });
      if (otpRes?.status === 200 && otpRes?.data?.status === 1) {
        toast.success("OTP resent to your email");
        setResendTimer(30); // 30 seconds
      } else {
        toast.error(otpRes?.data?.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to resend OTP");
    }
    setResendLoading(false);
  };

  // OTP change
  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    let newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Formik for email+password
  const formik = useFormik({
    initialValues: adminInitial,
    validationSchema: adminValidationSchema,
    onSubmit: async (values) => {
  try {
    setLoader(true);

    // 1️⃣ email+password check
    const loginRes = await loginAdmin({
      email: values.email,
      password: values.password,
    });

    if (loginRes?.status === 200 && loginRes?.data?.status === 1) {
      // 2️⃣ agar login success → OTP bhejna
      const otpRes = await sendOtpApi({ email: values.email });

      if (otpRes?.status === 200 && otpRes?.data?.status === 1) {
        toast.success("OTP sent to your email");
        setStep(2); // move to OTP step
      } else {
        toast.error(otpRes?.data?.message || "Failed to send OTP");
      }
    } else {
      toast.error(loginRes?.data?.message || "Invalid email or password");
    }
  } catch (error) {
    toast.error(error.response?.data?.message || "Something went wrong");
  }
  setLoader(false);
},

  });

  // ✅ Final OTP verification
  const handleOtpVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      toast.error("Please enter complete OTP");
      return;
    }
    try {
      setLoader(true);
      const res = await verifyOtpApi({ email: formik.values.email, otp: otpValue });
      if (res?.status === 200 && res?.data?.status === 1) {
        toast.success("Login Success!");
        localStorage.setItem("token", res?.data?.token);
        window.location = "/dashboard";
      } else {
        toast.error(res?.data?.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP verification failed");
    }
    setLoader(false);
  };

  return (
    <main>
      <section>
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-3 col-lg-5 col-md-7 col-sm-12 mx-auto">
              <div className="d-grid text-center">
                <div className="d-flex justify-content-center">
                  <img
                    src={"/images/appLogo_new.png"}
                    alt="logo"
                    height={200}
                    className="mt-2"
                  />
                </div>
                <br />
                <h2 className="sign-in">Sign In</h2>
                <p>Sign in to your admin account</p>
                <br />

                {/* STEP 1: EMAIL + PASSWORD */}
                {step === 1 && (
                  <form onSubmit={formik.handleSubmit}>
                    <label className="form-label text-start d-block">
                      Email address
                    </label>
                    <input
                      className="form-control"
                      type="email"
                      name="email"
                      placeholder="@example.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.email && formik.errors.email ? (
                      <div className="req-error">{formik.errors.email}</div>
                    ) : null}
                    <br />

                    <label className="form-label text-start d-block">
                      Password
                    </label>
                    <input
                      className="form-control"
                      type="password"
                      name="password"
                      placeholder="Enter your password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.password && formik.errors.password ? (
                      <div className="req-error">{formik.errors.password}</div>
                    ) : null}
                    <br />

                    <button type="submit" className="btn-custom" disabled={loader}>
                      {loader ? <Spinwheel sizeClass="spin-small" /> : "SIGN IN"}
                    </button>
                  </form>
                )}

                {/* STEP 2: OTP */}
                {step === 2 && (
                  <>
                    <p>Enter the 6-digit OTP sent to {formik.values.email}</p>
                    
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
  {otp.map((digit, idx) => (
    <input
      key={idx}
      type="text"
      maxLength="1"
      value={digit}
      ref={(el) => (inputsRef.current[idx] = el)}
      onChange={(e) => handleOtpChange(e.target.value, idx)}
      onKeyDown={(e) => {
        if (e.key === "Backspace" && !otp[idx] && idx > 0) {
          inputsRef.current[idx - 1].focus();
        }
        // Trigger OTP verify on Enter if all digits are filled
        if (e.key === "Enter") {
          const otpValue = otp.join("");
          if (otpValue.length === 6 && otp.every(d => d !== "")) {
            handleOtpVerify();
          }
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        const paste = e.clipboardData.getData("text").slice(0, 6).split("");
        const newOtp = [...otp];
        paste.forEach((char, i) => {
          if (/^[0-9]$/.test(char) && idx + i < 6) {
            newOtp[idx + i] = char;
          }
        });
        setOtp(newOtp);
        // last filled index pr focus
        const nextIndex = idx + paste.length - 1;
        if (nextIndex < 6) {
          inputsRef.current[nextIndex].focus();
        }
      }}
      style={{
        width: "40px",
        height: "40px",
        textAlign: "center",
        fontSize: "18px",
      }}
    />
  ))}
</div>
{/* Resend OTP Button with timer */}
                    <div className="mb-2" style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
                      <span style={{fontWeight:600}}>Don't Receive the OTP?</span>
                      <button
                        type="button"
                        className="btn btn-link"
                        style={{padding:0, fontWeight:600, fontSize:"16px"}}
                        onClick={handleResendOtp}
                        disabled={resendTimer > 0 || resendLoading}
                      >
                        {resendTimer > 0
                          ? `Resend OTP (${Math.floor(resendTimer/60)}:${(resendTimer%60).toString().padStart(2,'0')})`
                          : resendLoading
                            ? <Spinwheel sizeClass="spin-small" />
                            : "Resend OTP"}
                      </button>
                    </div>

                    <br />
                    <button
                      type="button"
                      onClick={handleOtpVerify}
                      className="btn-custom"
                      disabled={loader}
                    >
                      {loader ? <Spinwheel sizeClass="spin-small" /> : "Verify OTP"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Login;
